import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { agentExecutionState, AgentExecutionState } from "@/utils/agent-executions";
import type { AgentExecution_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { useRunner } from "@/queries/runner-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { isManagedRunner } from "@/utils/runner-detection";
import { useFlagIsSentryEnabled } from "@/hooks/use-flag";
import { type Runner } from "gitpod-next-api/gitpod/v1/runner_pb.ts";
import { type PlainMessage } from "@bufbuild/protobuf";
import type { LastBlockActivityContext } from "@/hooks/use-stream-conversation";

const STUCK_CONVERSATION_TIMEOUT_SECONDS = 30 * 1000; // 30s

type StuckDetectionContext = {
    id: string;
    name?: string;
    agentId?: string;
    environmentId?: string;
    desiredPhase?: AgentExecution_Phase;
    currentPhase?: AgentExecution_Phase;
    failureMessage?: string;
    runnerId?: string;
    runnerProvider?: string;
    runnerKind?: string;
    runnerManagerId?: string;
    isManagedRunner: boolean;
    lastBlockContext?: LastBlockActivityContext;
} | null;

const createStuckDetectionContext = (
    agentExecution: PlainAgentExecution | null,
    runner: PlainMessage<Runner> | undefined,
    runnerIsManaged: boolean,
    lastBlockContext: LastBlockActivityContext | undefined,
): StuckDetectionContext => {
    if (!agentExecution) return null;

    return {
        id: agentExecution.id,
        name: agentExecution.metadata?.name,
        agentId: agentExecution.spec?.agentId,
        environmentId:
            agentExecution.spec?.codeContext?.context?.case === "environmentId"
                ? agentExecution.spec.codeContext.context.value
                : undefined,
        desiredPhase: agentExecution.spec?.desiredPhase,
        currentPhase: agentExecution.status?.phase,
        failureMessage: agentExecution.status?.failureMessage,
        runnerId: runner?.runnerId,
        runnerProvider: runner?.provider.toString(),
        runnerKind: runner?.kind.toString(),
        runnerManagerId: runner?.runnerManagerId,
        isManagedRunner: runnerIsManaged,
        lastBlockContext: lastBlockContext,
    };
};

/**
 * Detects stuck conversations and reports them to Sentry.
 * Reports stuck conversations if:
 * - The runner is managed by Gitpod, OR
 * - The organization has the Sentry feature flag enabled
 *
 * @param agentExecution - The agent execution to monitor. If null, no detection is performed.
 * @param lastAgentBlockContext - Is the last block received by an agent.
 */
export function useStuckConversationDetection(
    agentExecution: PlainAgentExecution | null,
    lastAgentBlockContext?: LastBlockActivityContext,
) {
    const reportedStuckRef = useRef<boolean>(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastExecutionIdRef = useRef<string | null>(null);
    const lastAgentActivityTimestampRef = useRef<number>(0);

    // Get environment and runner information to determine if this is a managed runner
    const environmentId =
        agentExecution?.spec?.codeContext?.context?.case === "environmentId"
            ? agentExecution.spec.codeContext.context.value
            : undefined;

    const { data: environment } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);
    const { value: isSentryFeatureFlagEnabled } = useFlagIsSentryEnabled();
    const runnerIsManaged = isManagedRunner(runner);

    const shouldReportStuckConversation = useMemo(() => {
        return runnerIsManaged || isSentryFeatureFlagEnabled;
    }, [runnerIsManaged, isSentryFeatureFlagEnabled]);

    const detectionContext = useMemo(
        () => createStuckDetectionContext(agentExecution, runner, runnerIsManaged, lastAgentBlockContext),
        [agentExecution, runner, runnerIsManaged, lastAgentBlockContext],
    );

    const currentState = useMemo(() => (agentExecution ? agentExecutionState(agentExecution) : null), [agentExecution]);

    const resetStuckCheck = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        lastExecutionIdRef.current = null;
        lastAgentActivityTimestampRef.current = 0;
    };

    const reportStuckConversation = useCallback(
        (context: StuckDetectionContext, currentState: AgentExecutionState) => {
            if (!context || !shouldReportStuckConversation) return;

            const lastActivityTS = new Date(context.lastBlockContext?.timestamp ?? 0).toISOString();
            Sentry.withScope((scope) => {
                scope.setTag("feature", "conversation");
                scope.setTag("issue_type", "stuck_conversation");
                scope.setLevel("warning");
                scope.setContext("environment", {
                    id: context.environmentId,
                });
                scope.setContext("conversation", {
                    agentExecutionId: context.id,
                    agentExecutionName: context.name,
                    state: currentState,
                    lastAgentActivityTS: lastActivityTS,
                    agentId: context.agentId,
                    environmentId: context.environmentId,
                    stuckForSeconds: STUCK_CONVERSATION_TIMEOUT_SECONDS / 1000,
                });
                scope.setContext("agent_activity", {
                    lastActivityTS: lastActivityTS,
                    lastToolStatus: context.lastBlockContext?.status,
                    lastToolCallID: context.lastBlockContext?.id,
                });
                scope.setContext("runner", {
                    isManagedRunner: context.isManagedRunner,
                    runnerId: context.runnerId,
                    runnerKind: context.runnerKind,
                    runnerManagerId: context.runnerManagerId,
                    runnerProvider: context.runnerProvider,
                });

                Sentry.captureException(new Error("Conversation appears stuck"));
            });
        },
        [shouldReportStuckConversation],
    );

    useEffect(() => {
        if (!shouldReportStuckConversation) {
            return;
        }

        // Only track stuck conversations when agent is actively working (RUNNING phase).
        // Ignore PENDING state to reduce noise - pending agents will be tracked when they start running.
        const agentRunning = currentState === AgentExecutionState.WORKING;

        // Don't consider conversation stuck if the last agent block was a tool call (actionStarted).
        // This indicates the agent is actively using tools and may be waiting for tool results.
        const isLastBlockToolCall =
            lastAgentBlockContext?.id?.startsWith("tool") && lastAgentBlockContext?.status === "actionStarted";
        const shouldTrackStuckConversation = agentRunning && !isLastBlockToolCall;

        // Reset when agent execution changes, stops running, last block is a tool call, or no block context.
        if (!detectionContext || !shouldTrackStuckConversation || !lastAgentBlockContext) {
            resetStuckCheck();
            return;
        }

        // Reset when agent execution ID changes.
        if (detectionContext.id !== lastExecutionIdRef.current) {
            resetStuckCheck();
            lastExecutionIdRef.current = detectionContext.id;
            reportedStuckRef.current = false;
        }

        const currentTimestamp = lastAgentBlockContext?.timestamp ?? 0;
        if (lastAgentActivityTimestampRef.current !== currentTimestamp) {
            lastAgentActivityTimestampRef.current = currentTimestamp;
            resetStuckCheck();

            timeoutRef.current = setTimeout(() => {
                if (!reportedStuckRef.current) {
                    reportedStuckRef.current = true;
                    reportStuckConversation(detectionContext, currentState);
                }
                resetStuckCheck();
            }, STUCK_CONVERSATION_TIMEOUT_SECONDS);
        }
    }, [currentState, detectionContext, lastAgentBlockContext, reportStuckConversation, shouldReportStuckConversation]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);
}
